import { ref, onBeforeUnmount, type Ref } from 'vue';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getServerCurrentTime } from '@/common/api/time';

import { GLOBAL_TIME_LIVE_FORMAT } from '@/common/utils/core/time/constants';
import {
  subscribeServerTime,
  setServerTimeBase,
  type ServerTimeSubscription,
} from '@/common/composables/serverTime/serverTime.bridge';

// 서버 시각 문자열('YYYY.MM.DD HH:mm:ss' 등)을 dayjs.tz(str, format) 으로 파싱하기 위해 필요.
dayjs.extend(customParseFormat);

export interface UseServerTimeOptions {
  /** 업데이트 주기 (ms), 기본값 5000 */
  interval?: number;
  /** 시간 포맷, 기본값 GLOBAL_TIME_LIVE_FORMAT */
  format?: string;
  /** 탭 숨김 시 일시정지 여부, 기본값 true */
  pauseOnHidden?: boolean;
}

export interface UseServerTimeReturn {
  currentTime: Ref<string>;
  currentDate: Ref<Date>;
  isRunning: Ref<boolean>;
  start: (intervalMs?: number) => Promise<void>;
  stop: () => Promise<void>;
  setTime: (time: string) => void;
  cleanup: () => void;
}

/**
 * 서버 시간 관리 composable
 *
 * 모듈 레벨 싱글톤 Worker(`serverTime.bridge.ts`)를 구독하여 서버 시간을 주기적으로 업데이트합니다.
 * 다수의 컴포넌트가 동시에 사용해도 워커는 한 개만 실행되며, 마지막 구독자 cleanup 시 워커가 종료됩니다.
 * visibility API로 탭이 숨겨지면 자동 일시정지합니다.
 */
export const useServerTime = (
  options: UseServerTimeOptions = {},
  onTick?: (time: string) => void,
): UseServerTimeReturn => {
  const { interval = 5000, format = GLOBAL_TIME_LIVE_FORMAT, pauseOnHidden = true } = options;

  const initialNow = dayjs.tz();
  const currentTime = ref<string>(initialNow.format(format));
  // currentDate 는 currentTime 의 Date 본형. format 결합 없이 timestamp 계산이 필요한
  // 호출자(예: 자정 경계 감지) 가 currentTime string 을 재파싱하지 않도록 함께 노출한다.
  const currentDate = ref<Date>(initialNow.toDate());
  const isRunning = ref(false);

  let currentInterval = interval;
  let isVisible = true;
  let wasRunningBeforeHidden = false;

  // serverTime 은 사용자 timezone 의 wall-clock 문자열(타임존 표기 없음). 이를 instant 로
  // 만들 때 new Date(문자열) 을 쓰면 브라우저 로컬 타임존으로 파싱되어, 사용자 timezone 과
  // 다를 때 자정 경계 day-key 가 offset 만큼 어긋난다 (DSP-42235). format 으로 시각 컴포넌트를
  // 파싱한 뒤 사용자 timezone(dayjs default) 기준 instant 로 재해석한다.
  // (dayjs.tz(str, format) 은 2번째 인자를 timezone 으로 오인하므로 쓸 수 없다.)
  const wallClockToDate = (wallClock: string): Date =>
    dayjs.tz(dayjs(wallClock, format).format('YYYY-MM-DDTHH:mm:ss')).toDate();

  const handleTick = (time: Date): void => {
    currentDate.value = time;
    // 표시용 currentTime 은 사용자 timezone 기준이어야 한다. dayjs(Date) 는 브라우저 로컬
    // 타임존으로 포맷하므로, 사용자 timezone 과 다를 때 시각 표시가 어긋난다.
    currentTime.value = dayjs.tz(time).format(format);
    onTick?.(currentTime.value);
  };

  // subscription 은 lazy 등록: 컴포넌트 외부(Pinia store, 일반 TS 모듈)에서 사용해
  // onBeforeUnmount 가 동작하지 않는 경우에도 start 가 호출되지 않으면 Map 에 잔류하지 않는다.
  let subscription: ServerTimeSubscription | null = null;

  const ensureSubscription = (): ServerTimeSubscription => {
    if (!subscription) {
      subscription = subscribeServerTime(currentInterval, handleTick);
    }
    return subscription;
  };

  const start = async (intervalMs?: number): Promise<void> => {
    const sub = ensureSubscription();

    if (intervalMs !== undefined && intervalMs !== currentInterval) {
      currentInterval = intervalMs;
      await sub.updateInterval(intervalMs);
    }

    const serverTime = await getServerCurrentTime();
    // getServerCurrentTime await 도중 컴포넌트가 unmount(cleanup) 되면 subscription 이 null 이 된다.
    // 이미 정리된 구독에 대해 setServerTimeBase / sub.start 를 진행하면 워커가 다시 깨어날 수 있으므로
    // 즉시 중단한다.
    if (!subscription) return;
    currentTime.value = serverTime;
    const baseDate = wallClockToDate(serverTime);
    currentDate.value = baseDate;

    // worker 는 별도 스레드라 dayjs default timezone 이 없으므로, 타임존-안전한 ISO(UTC)
    // 문자열로 baseTime 을 넘겨 동일 instant 로 파싱되게 한다 (DSP-42235).
    await setServerTimeBase(baseDate.toISOString());
    // 워커 첫 정기 tick 은 interval(기본 5초) 뒤에 도착하므로, 그동안 호출자 표시가 직전 시각으로
    // 멈춰 보이지 않도록 bridge 에 initialTime 을 넘겨 본인 callback 을 즉시 1 회 호출한다.
    // (다중 구독자 환경에서 다른 구독자에게는 영향 없음)
    await sub.start(baseDate);
    isRunning.value = true;
  };

  const stop = async (): Promise<void> => {
    isRunning.value = false;
    // stop 은 LiveUpdater 등에서 await 없이 fire-and-forget 으로 호출된다. reconcile 실패가
    // unhandled rejection 으로 새어 나가지 않도록 흡수한다 (화면 정지 실패는 치명적이지 않음).
    try {
      await subscription?.stop();
    } catch (e) {
      console.error('[useServerTime] stop failed:', e);
    }
  };

  /**
   * 서버 시간 base 를 직접 갱신한다.
   *
   * 내부적으로 모듈 싱글톤 워커의 `setServerTimeBase` 를 호출하기 때문에 **모든 구독자의
   * 다음 tick 기준 시간이 함께 갱신**되는 글로벌 동작이다. (기존 인스턴스별 워커 모델의 setTime 과
   * 다른 점)
   *
   * 호출자가 await 하지 않는 fire-and-forget 경로라, 워커 호출 실패가 한 구독자가 아니라
   * 전체 구독자의 base 갱신 실패로 이어진다. 실패가 묻혀 디버깅이 어려워지지 않도록
   * 콘솔로 노출한다.
   */
  const setTime = (time: string): void => {
    currentTime.value = time;
    // start() 와 동일하게 사용자 timezone 기준으로 파싱한 뒤 ISO(UTC) 로 worker 에 전달.
    const baseDate = wallClockToDate(time);
    currentDate.value = baseDate;
    setServerTimeBase(baseDate.toISOString()).catch((e) => {
      console.error('[useServerTime] setServerTimeBase failed:', e);
    });
  };

  const cleanup = (): void => {
    if (!subscription) return;
    subscription.cleanup();
    subscription = null;
    isRunning.value = false;
  };

  const handleVisibilityChange = async (): Promise<void> => {
    const prevIsVisible = isVisible;
    isVisible = document.visibilityState === 'visible';

    if (!prevIsVisible && isVisible && wasRunningBeforeHidden) {
      await start(currentInterval);
      wasRunningBeforeHidden = false;
    } else if (prevIsVisible && !isVisible && isRunning.value) {
      wasRunningBeforeHidden = true;
      await stop();
    }
  };

  if (pauseOnHidden) {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  onBeforeUnmount(() => {
    if (pauseOnHidden) {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    cleanup();
  });

  return { currentTime, currentDate, isRunning, start, stop, setTime, cleanup };
};
